import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Phone,
  PhoneOff,
  PhoneCall,
  PhoneForwarded,
  Voicemail,
  Upload,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Archive,
  RotateCcw,
  BarChart3,
  Settings,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Send,
  Mail,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  MapPin,
  Star,
  Home,
  Smartphone,
  Tv,
  HelpCircle,
  Download,
  CalendarDays,
  FolderKanban,
  Building2,
  CalendarIcon
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { cn } from "../lib/utils";

// All available columns
const ALL_COLUMNS = [
  { key: "customer_name", label: "Customer", default: true },
  { key: "customer_number", label: "Phone", default: true },
  { key: "alternate_number", label: "Alt. Number", default: false },
  { key: "assigned_resources", label: "Technician", default: true },
  { key: "appointment_type", label: "Type", default: true },
  { key: "job_description", label: "Description", default: false },
  { key: "street", label: "Street", default: false },
  { key: "city", label: "City", default: true },
  { key: "province", label: "Province", default: true },
  { key: "duration", label: "Duration", default: false },
  { key: "date", label: "Date", default: false },
  { key: "ncid", label: "NCID", default: false },
  { key: "call_id_woid", label: "Call ID/WOID", default: false },
  { key: "appointment_window", label: "Window", default: false },
  { key: "attempt_count", label: "Attempts", default: true },
];

export const QACallsModule = ({ user }) => {
  const [activeTab, setActiveTab] = useState("today");
  const [todayCalls, setTodayCalls] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("qa_visible_columns");
    if (saved) return JSON.parse(saved);
    return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  
  // Filters
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [selectedTechnicians, setSelectedTechnicians] = useState([]); // Multi-select array
  const [showTechDropdown, setShowTechDropdown] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [technicians, setTechnicians] = useState([]);
  
  // Calendar View State
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [dayCallLogs, setDayCallLogs] = useState([]);
  
  // Projects State
  const [projects, setProjects] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: "",
    provinces: [],
    supervisor_email: "",
    supervisor_name: "",
    description: ""
  });
  
  // Dialogs
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  
  // Selected items
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Call form
  const [callForm, setCallForm] = useState({
    picked_up: false,
    rating: null,
    sales_pitch_made: null, // null, true, false
    sales_pitch_type: [], // Mobility, Smart Home, Custom Home, Stream+, Others
    no_pitch_reason: null, // Customer Not Interested, Others
    comment: "",
    call_outcome: null // Complete Call, Left a Voicemail, Call Not Picked Up
  });
  
  // Import
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  
  // Settings form - updated for multiple manager emails
  const [settingsForm, setSettingsForm] = useState({
    max_attempts: 3,
    days_between_retries: 1,
    auto_rollover_enabled: true,
    manager_emails: [],
    newManagerEmail: "",
    technician_emails: []
  });
  const [newTechMapping, setNewTechMapping] = useState({ technician_name: "", technician_email: "" });

  const isAdmin = user?.role === "admin";

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem("qa_visible_columns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [todayRes, reportRes, statsRes, provincesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/qa/today`, { withCredentials: true }),
        axios.get(`${API}/qa/reports/daily`, { withCredentials: true }),
        axios.get(`${API}/qa/my-stats`, { withCredentials: true }),
        axios.get(`${API}/qa/provinces`, { withCredentials: true }),
        axios.get(`${API}/qa/projects`, { withCredentials: true })
      ]);
      
      setTodayCalls(todayRes.data);
      setDailyReport(reportRes.data);
      setMyStats(statsRes.data);
      setProvinces(provincesRes.data);
      setProjects(projectsRes.data);
      
      // Extract unique technicians from today's calls
      const uniqueTechs = [...new Set(todayRes.data.map(c => c.assigned_resources).filter(Boolean))].sort();
      setTechnicians(uniqueTechs);
      
      if (isAdmin) {
        const [customersRes, weeklyRes, settingsRes, emailStatusRes] = await Promise.all([
          axios.get(`${API}/qa/customers`, { withCredentials: true }),
          axios.get(`${API}/qa/reports/weekly`, { withCredentials: true }),
          axios.get(`${API}/qa/settings`, { withCredentials: true }),
          axios.get(`${API}/qa/email-status`, { withCredentials: true }).catch(() => ({ data: { configured: false } }))
        ]);
        
        setAllCustomers(customersRes.data);
        setWeeklyReport(weeklyRes.data);
        setSettings(settingsRes.data);
        setEmailStatus(emailStatusRes.data);
        setSettingsForm({
          max_attempts: settingsRes.data.max_attempts,
          days_between_retries: settingsRes.data.days_between_retries,
          auto_rollover_enabled: settingsRes.data.auto_rollover_enabled,
          manager_emails: settingsRes.data.manager_emails || [],
          newManagerEmail: "",
          technician_emails: settingsRes.data.technician_emails || []
        });
      }
    } catch (error) {
      console.error("Error fetching QA data:", error);
      toast.error("Failed to load QA data");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch calendar data for a month
  const fetchCalendarData = useCallback(async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const res = await axios.get(`${API}/qa/calls/by-month/${year}/${month}`, { withCredentials: true });
      setCalendarData(res.data);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  }, []);

  // Fetch calls for a specific date
  const fetchDayCallLogs = useCallback(async (date) => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/qa/calls/by-date/${dateStr}`, { withCredentials: true });
      setDayCallLogs(res.data);
    } catch (error) {
      console.error("Error fetching day calls:", error);
      setDayCallLogs([]);
    }
  }, []);

  // Update calendar data when month changes
  useEffect(() => {
    if (showCalendarView) {
      fetchCalendarData(calendarMonth);
    }
  }, [showCalendarView, calendarMonth, fetchCalendarData]);

  // Fetch day calls when date is selected
  useEffect(() => {
    if (selectedCalendarDate) {
      fetchDayCallLogs(selectedCalendarDate);
    }
  }, [selectedCalendarDate, fetchDayCallLogs]);

  // Export calls to Excel
  const handleExportCalls = async () => {
    try {
      const response = await axios.get(`${API}/qa/export/calls`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qa_calls_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  // Handle file import
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }
    
    setIsImporting(true);
    const formData = new FormData();
    formData.append("file", importFile);
    
    try {
      const response = await axios.post(`${API}/qa/import`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Imported ${response.data.imported} customers from ${response.data.provinces?.join(", ") || "file"}`);
      setShowImportDialog(false);
      setImportFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setIsImporting(false);
    }
  };

  // Handle call log
  const handleLogCall = async () => {
    if (!selectedCustomer) return;
    
    // Validate call outcome is selected
    if (!callForm.call_outcome) {
      toast.error("Please select a call outcome (Complete Call, Left a Voicemail, or Call Not Picked Up)");
      return;
    }
    
    // If call was picked up (Complete Call), rating is required
    if (callForm.call_outcome === "Complete Call" && !callForm.rating) {
      toast.error("Please select a customer rating");
      return;
    }
    
    // If rating is given, sales pitch info is required
    if (callForm.rating && callForm.sales_pitch_made === null) {
      toast.error("Please indicate if a sales pitch was made");
      return;
    }
    
    try {
      // Prepare form data
      const submitData = {
        picked_up: callForm.call_outcome === "Complete Call",
        rating: callForm.rating,
        sales_pitch_made: callForm.sales_pitch_made === true,
        sales_pitch_type: callForm.sales_pitch_type,
        no_pitch_reason: callForm.no_pitch_reason,
        comment: callForm.comment,
        call_outcome: callForm.call_outcome
      };
      
      const response = await axios.post(
        `${API}/qa/customers/${selectedCustomer.customer_id}/call`,
        submitData,
        { withCredentials: true }
      );
      
      const outcomeMessage = {
        "Complete Call": "Call completed!",
        "Left a Voicemail": "Voicemail logged - will retry",
        "Call Not Picked Up": "Call logged - will retry"
      };
      toast.success(outcomeMessage[callForm.call_outcome]);
      
      // Store the call for potential notification
      if (callForm.call_outcome === "Complete Call" && callForm.rating === "POOR") {
        setSelectedCall(response.data.call_log);
      }
      
      setShowCallDialog(false);
      setSelectedCustomer(null);
      setCallForm({ 
        picked_up: false, 
        rating: null, 
        sales_pitch_made: null, 
        sales_pitch_type: [],
        no_pitch_reason: null,
        comment: "",
        call_outcome: null
      });
      fetchData();
      // Refresh calendar if open
      if (showCalendarView) {
        fetchCalendarData(calendarMonth);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log call");
    }
  };

  // Handle send notification to tech
  const handleSendToTech = async (customerId, callId) => {
    try {
      const response = await axios.post(
        `${API}/qa/customers/${customerId}/send-notification?call_id=${callId}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    }
  };

  // Project management functions
  const handleSaveProject = async () => {
    try {
      if (editingProject) {
        await axios.patch(`${API}/qa/projects/${editingProject.project_id}`, projectForm, { withCredentials: true });
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/qa/projects`, projectForm, { withCredentials: true });
        toast.success("Project created");
      }
      setShowProjectDialog(false);
      setEditingProject(null);
      setProjectForm({ name: "", provinces: [], supervisor_email: "", supervisor_name: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/qa/projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete project");
    }
  };

  // Handle archive/restore
  const handleArchive = async (customerId) => {
    try {
      await axios.post(`${API}/qa/customers/${customerId}/archive`, {}, { withCredentials: true });
      toast.success("Customer archived");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive");
    }
  };

  const handleRestore = async (customerId) => {
    try {
      await axios.post(`${API}/qa/customers/${customerId}/restore`, {}, { withCredentials: true });
      toast.success("Customer restored");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore");
    }
  };

  // Handle settings update
  const handleUpdateSettings = async () => {
    try {
      await axios.patch(`${API}/qa/settings`, settingsForm, { withCredentials: true });
      toast.success("Settings updated");
      setShowSettingsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    }
  };

  // Handle add technician mapping
  const handleAddTechMapping = () => {
    if (!newTechMapping.technician_name || !newTechMapping.technician_email) {
      toast.error("Please enter both technician name and email");
      return;
    }
    setSettingsForm({
      ...settingsForm,
      technician_emails: [...settingsForm.technician_emails, { ...newTechMapping }]
    });
    setNewTechMapping({ technician_name: "", technician_email: "" });
  };

  // Handle remove technician mapping
  const handleRemoveTechMapping = (index) => {
    const updated = settingsForm.technician_emails.filter((_, i) => i !== index);
    setSettingsForm({ ...settingsForm, technician_emails: updated });
  };

  // Handle rollover
  const handleRollover = async () => {
    try {
      const response = await axios.post(`${API}/qa/process-rollover`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rollover failed");
    }
  };

  // Toggle column visibility
  const toggleColumn = (key) => {
    setVisibleColumns(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      archived: "bg-gray-100 text-gray-800"
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const getRatingBadge = (rating) => {
    if (rating === "EXCELLENT") {
      return <Badge className="bg-blue-100 text-blue-800"><Star className="w-3 h-3 mr-1" />EXCELLENT</Badge>;
    } else if (rating === "GOOD") {
      return <Badge className="bg-green-100 text-green-800"><ThumbsUp className="w-3 h-3 mr-1" />GOOD</Badge>;
    } else if (rating === "POOR") {
      return <Badge className="bg-red-100 text-red-800"><ThumbsDown className="w-3 h-3 mr-1" />POOR</Badge>;
    }
    return null;
  };

  // Filter customers by province and technicians (multi-select)
  const filteredTodayCalls = todayCalls.filter(c => {
    const provinceMatch = provinceFilter === "all" || c.province === provinceFilter;
    const techMatch = selectedTechnicians.length === 0 || selectedTechnicians.includes(c.assigned_resources);
    return provinceMatch && techMatch;
  });

  const filteredCustomers = allCustomers.filter(c => {
    const statusMatch = customerFilter === "all" || c.status === customerFilter;
    const provinceMatch = provinceFilter === "all" || c.province === provinceFilter;
    const techMatch = selectedTechnicians.length === 0 || selectedTechnicians.includes(c.assigned_resources);
    return statusMatch && provinceMatch && techMatch;
  });

  // Toggle technician selection
  const toggleTechnician = (tech) => {
    setSelectedTechnicians(prev => 
      prev.includes(tech) 
        ? prev.filter(t => t !== tech)
        : [...prev, tech]
    );
  };

  // Clear all technician filters
  const clearTechnicianFilter = () => {
    setSelectedTechnicians([]);
  };

  // Group customers by province
  const customersByProvince = filteredTodayCalls.reduce((acc, customer) => {
    const prov = customer.province || "Unknown";
    if (!acc[prov]) acc[prov] = [];
    acc[prov].push(customer);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">QA Calls</h1>
          <p className="text-muted-foreground">Quality Assurance Follow-up Calls</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Province Filter */}
          <Select value={provinceFilter} onValueChange={setProvinceFilter}>
            <SelectTrigger className="w-[140px]" data-testid="province-filter">
              <MapPin className="w-4 h-4 mr-1" />
              <SelectValue placeholder="Province" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Provinces</SelectItem>
              {provinces.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Multi-Select Technician Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTechDropdown(!showTechDropdown)}
              className="w-[200px] justify-between"
              data-testid="technician-filter"
            >
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                {selectedTechnicians.length === 0 
                  ? "All Technicians" 
                  : `${selectedTechnicians.length} selected`}
              </div>
              <ChevronRight className={cn("w-4 h-4 transition-transform", showTechDropdown && "rotate-90")} />
            </Button>
            
            {showTechDropdown && (
              <div className="absolute top-full left-0 mt-1 w-[280px] bg-white border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
                <div className="p-2 border-b sticky top-0 bg-white">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Select Technicians</span>
                    {selectedTechnicians.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearTechnicianFilter} className="text-xs h-6">
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  {technicians.map(tech => (
                    <label 
                      key={tech} 
                      className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedTechnicians.includes(tech)}
                        onCheckedChange={() => toggleTechnician(tech)}
                      />
                      <span className="text-sm">{tech}</span>
                    </label>
                  ))}
                </div>
                <div className="p-2 border-t sticky bottom-0 bg-white">
                  <Button 
                    size="sm" 
                    className="w-full" 
                    onClick={() => setShowTechDropdown(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettingsDialog(true)}
                data-testid="qa-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportDialog(true)}
                data-testid="qa-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowColumnPicker(!showColumnPicker)}
            data-testid="column-picker-btn"
          >
            {showColumnPicker ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button size="sm" onClick={fetchData} data-testid="qa-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Column Picker */}
      {showColumnPicker && (
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3">Show/Hide Columns</h3>
          <div className="flex flex-wrap gap-3">
            {ALL_COLUMNS.map(col => (
              <label key={col.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={visibleColumns.includes(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                />
                <span className="text-sm">{col.label}</span>
              </label>
            ))}
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today's Calls</p>
                <p className="text-2xl font-bold">{filteredTodayCalls.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Picked Up</p>
                <p className="text-2xl font-bold">{dailyReport?.picked_up || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ThumbsUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">GOOD</p>
                <p className="text-2xl font-bold">{dailyReport?.good_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <ThumbsDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">POOR</p>
                <p className="text-2xl font-bold">{dailyReport?.poor_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pickup Rate</p>
                <p className="text-2xl font-bold">{dailyReport?.pickup_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid lg:grid-cols-6">
            <TabsTrigger value="today" data-testid="qa-tab-today">
              <PhoneCall className="w-4 h-4 mr-2 hidden sm:inline" />
              Today's Queue
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="qa-tab-calendar">
              <CalendarDays className="w-4 h-4 mr-2 hidden sm:inline" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="qa-tab-reports">
              <BarChart3 className="w-4 h-4 mr-2 hidden sm:inline" />
              Reports
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="projects" data-testid="qa-tab-projects">
                <FolderKanban className="w-4 h-4 mr-2 hidden sm:inline" />
                Projects
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="customers" data-testid="qa-tab-customers">
                <Users className="w-4 h-4 mr-2 hidden sm:inline" />
                All Customers
              </TabsTrigger>
            )}
            <TabsTrigger value="mystats" data-testid="qa-tab-mystats">
              <TrendingUp className="w-4 h-4 mr-2 hidden sm:inline" />
              My Stats
            </TabsTrigger>
          </TabsList>
          
          {/* Export Button */}
          <Button variant="outline" onClick={handleExportCalls} data-testid="export-calls-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Today's Queue Tab */}
        <TabsContent value="today" className="mt-6">
          {Object.keys(customersByProvince).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No calls scheduled for today</p>
                {isAdmin && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowImportDialog(true)}
                  >
                    Import Customer List
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(customersByProvince).map(([prov, customers]) => (
                <Card key={prov}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MapPin className="w-5 h-5" />
                      {prov} ({customers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-[500px] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(col => (
                                <TableHead key={col.key}>{col.label}</TableHead>
                              ))}
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {customers.map((customer) => (
                              <TableRow key={customer.customer_id}>
                                {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(col => (
                                  <TableCell key={col.key} className={col.key === "customer_name" ? "font-medium" : ""}>
                                    {col.key === "attempt_count" ? (
                                      <Badge variant="outline">{customer.attempt_count}/{settings?.max_attempts || 3}</Badge>
                                    ) : (
                                      customer[col.key] || "-"
                                    )}
                                  </TableCell>
                                ))}
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCustomer(customer);
                                      setShowCallDialog(true);
                                    }}
                                    data-testid={`call-btn-${customer.customer_id}`}
                                  >
                                    <Phone className="w-4 h-4 mr-2" />
                                    Log Call
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Calendar View Tab */}
        <TabsContent value="calendar" className="mt-6">
          <div className="grid lg:grid-cols-[400px_1fr] gap-6">
            {/* Calendar */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Select Date</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-medium min-w-[120px] text-center">
                      {format(calendarMonth, "MMMM yyyy")}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const start = startOfMonth(calendarMonth);
                    const end = endOfMonth(calendarMonth);
                    const days = eachDayOfInterval({ start, end });
                    
                    // Add padding for first day of month
                    const firstDayOfWeek = start.getDay() || 7; // Convert Sunday (0) to 7
                    const padding = Array(firstDayOfWeek - 1).fill(null);
                    
                    return [...padding, ...days].map((day, idx) => {
                      if (!day) {
                        return <div key={`pad-${idx}`} className="p-2" />;
                      }
                      
                      const dateStr = format(day, "yyyy-MM-dd");
                      const dayData = calendarData[dateStr] || {};
                      const hasData = dayData.total > 0;
                      const isToday = isSameDay(day, new Date());
                      const isSelected = selectedCalendarDate && isSameDay(day, selectedCalendarDate);
                      
                      return (
                        <button
                          key={dateStr}
                          onClick={() => setSelectedCalendarDate(day)}
                          className={cn(
                            "p-2 rounded-lg text-center transition-colors min-h-[60px]",
                            isToday && "ring-2 ring-primary",
                            isSelected && "bg-primary text-primary-foreground",
                            !isSelected && hasData && "bg-blue-50 hover:bg-blue-100",
                            !isSelected && !hasData && "hover:bg-muted"
                          )}
                        >
                          <span className="text-sm font-medium">{format(day, "d")}</span>
                          {hasData && !isSelected && (
                            <div className="text-xs mt-1">
                              <span className="text-blue-600">{dayData.total}</span>
                            </div>
                          )}
                        </button>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Day Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  {selectedCalendarDate 
                    ? `Calls on ${format(selectedCalendarDate, "EEEE, MMMM d, yyyy")}`
                    : "Select a date to view calls"
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCalendarDate ? (
                  dayCallLogs.length > 0 ? (
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-3">
                        {dayCallLogs.map((call) => (
                          <div
                            key={call.call_id}
                            className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium">{call.customer_name || "Unknown"}</span>
                                  {call.rating && getRatingBadge(call.rating)}
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {call.customer_number}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {call.technician || "N/A"}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {call.city}, {call.province}
                                  </div>
                                  <div>
                                    <Badge variant="outline" className="text-xs">
                                      {call.call_outcome || "N/A"}
                                    </Badge>
                                  </div>
                                </div>
                                {call.sales_pitch_type?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {call.sales_pitch_type.map(type => (
                                      <Badge key={type} variant="secondary" className="text-xs">
                                        {type}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                {call.comment && (
                                  <p className="mt-2 text-sm text-muted-foreground italic">
                                    "{call.comment}"
                                  </p>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                by {call.agent_name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No calls logged on this date</p>
                    </div>
                  )
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Click on a date to view call logs</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Daily Report */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Daily Report - {dailyReport?.date}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-secondary/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-xl font-bold">{dailyReport?.total_calls || 0}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Picked Up</p>
                    <p className="text-xl font-bold text-green-700">{dailyReport?.picked_up || 0}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">GOOD Ratings</p>
                    <p className="text-xl font-bold text-green-700">{dailyReport?.good_count || 0}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">POOR Ratings</p>
                    <p className="text-xl font-bold text-red-700">{dailyReport?.poor_count || 0}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Sales Pitch Made</p>
                    <p className="text-xl font-bold text-purple-700">{dailyReport?.sales_pitch_count || 0}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Notifications Sent</p>
                    <p className="text-xl font-bold text-blue-700">{dailyReport?.notifications_sent || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weekly Report (Admin only) */}
            {isAdmin && weeklyReport && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Weekly Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{weeklyReport.period}</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-secondary/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Calls</p>
                      <p className="text-xl font-bold">{weeklyReport.total_calls}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Pickup Rate</p>
                      <p className="text-xl font-bold text-green-700">{weeklyReport.pickup_rate}%</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">GOOD Rate</p>
                      <p className="text-xl font-bold text-green-700">{weeklyReport.good_rate}%</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">POOR Count</p>
                      <p className="text-xl font-bold text-red-700">{weeklyReport.poor_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* All Customers Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="customers" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    All QA Customers ({filteredCustomers.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="w-[130px]" data-testid="customer-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={handleRollover} data-testid="rollover-btn">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Rollover
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Technician</TableHead>
                        <TableHead>Province</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCustomers.map((customer) => (
                        <TableRow key={customer.customer_id}>
                          <TableCell className="font-medium">{customer.customer_name}</TableCell>
                          <TableCell>{customer.customer_number}</TableCell>
                          <TableCell>{customer.appointment_type || "-"}</TableCell>
                          <TableCell>{customer.assigned_resources}</TableCell>
                          <TableCell><Badge variant="outline">{customer.province}</Badge></TableCell>
                          <TableCell>{getStatusBadge(customer.status)}</TableCell>
                          <TableCell>{customer.attempt_count}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setShowCustomerDetail(true);
                                }}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                              {customer.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleArchive(customer.customer_id)}
                                >
                                  <Archive className="w-4 h-4" />
                                </Button>
                              )}
                              {customer.status === "archived" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestore(customer.customer_id)}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Projects Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="projects" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5" />
                    QA Projects
                  </CardTitle>
                  <Button
                    onClick={() => {
                      setEditingProject(null);
                      setProjectForm({ name: "", provinces: [], supervisor_email: "", supervisor_name: "", description: "" });
                      setShowProjectDialog(true);
                    }}
                    data-testid="create-project-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Assign provinces to projects and set supervisors. Employees assigned to a project will only see calls from those provinces.
                </p>
              </CardHeader>
              <CardContent>
                {projects.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {projects.map((project) => (
                      <Card key={project.project_id} className="border-2">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-primary" />
                                {project.name}
                              </CardTitle>
                              {project.description && (
                                <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingProject(project);
                                  setProjectForm({
                                    name: project.name,
                                    provinces: project.provinces || [],
                                    supervisor_email: project.supervisor_email || "",
                                    supervisor_name: project.supervisor_name || "",
                                    description: project.description || ""
                                  });
                                  setShowProjectDialog(true);
                                }}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive"
                                onClick={() => handleDeleteProject(project.project_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Provinces</p>
                              <div className="flex flex-wrap gap-1">
                                {project.provinces?.length > 0 ? (
                                  project.provinces.map(prov => (
                                    <Badge key={prov} variant="secondary">{prov}</Badge>
                                  ))
                                ) : (
                                  <span className="text-sm text-muted-foreground">No provinces assigned</span>
                                )}
                              </div>
                            </div>
                            {project.supervisor_email && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Supervisor</p>
                                <div className="flex items-center gap-2">
                                  <Mail className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-sm">{project.supervisor_name || project.supervisor_email}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No projects created yet</p>
                    <p className="text-sm mt-1">Create a project to organize QA calls by province</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* My Stats Tab */}
        <TabsContent value="mystats" className="mt-6">
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Today</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Calls</span>
                  <span className="font-semibold">{myStats?.today?.total_calls || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Picked Up</span>
                  <span className="font-semibold text-green-600">{myStats?.today?.picked_up || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pickup Rate</span>
                  <span className="font-semibold">{myStats?.today?.pickup_rate || 0}%</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">This Week</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Calls</span>
                  <span className="font-semibold">{myStats?.this_week?.total_calls || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Picked Up</span>
                  <span className="font-semibold text-green-600">{myStats?.this_week?.picked_up || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pickup Rate</span>
                  <span className="font-semibold">{myStats?.this_week?.pickup_rate || 0}%</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">All Time</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Calls</span>
                  <span className="font-semibold">{myStats?.all_time?.total_calls || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Picked Up</span>
                  <span className="font-semibold text-green-600">{myStats?.all_time?.picked_up || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GOOD Ratings</span>
                  <span className="font-semibold text-green-600">{myStats?.all_time?.good_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">POOR Ratings</span>
                  <span className="font-semibold text-red-600">{myStats?.all_time?.poor_count || 0}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Call Logging Dialog */}
      <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Log QA Call</DialogTitle>
            <DialogDescription>
              {selectedCustomer?.customer_name} - {selectedCustomer?.customer_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto">
            {/* Customer Info */}
            <div className="p-3 bg-secondary/50 rounded-lg grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium">{selectedCustomer?.appointment_type || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Technician</p>
                <p className="font-medium">{selectedCustomer?.assigned_resources}</p>
              </div>
              <div>
                <p className="text-muted-foreground">City</p>
                <p className="font-medium">{selectedCustomer?.city || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Province</p>
                <p className="font-medium">{selectedCustomer?.province}</p>
              </div>
            </div>
            
            {/* Customer Rating - Always show */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Customer Rating</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={callForm.rating === "EXCELLENT" ? "default" : "outline"}
                  className={cn(
                    "flex-1",
                    callForm.rating === "EXCELLENT" && "bg-blue-600 hover:bg-blue-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, rating: "EXCELLENT" })}
                  data-testid="rating-excellent-btn"
                >
                  <Star className="w-4 h-4 mr-1" />
                  Excellent
                </Button>
                <Button
                  type="button"
                  variant={callForm.rating === "GOOD" ? "default" : "outline"}
                  className={cn(
                    "flex-1",
                    callForm.rating === "GOOD" && "bg-green-600 hover:bg-green-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, rating: "GOOD" })}
                  data-testid="rating-good-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Good
                </Button>
                <Button
                  type="button"
                  variant={callForm.rating === "POOR" ? "default" : "outline"}
                  className={cn(
                    "flex-1",
                    callForm.rating === "POOR" && "bg-red-600 hover:bg-red-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, rating: "POOR" })}
                  data-testid="rating-poor-btn"
                >
                  <ThumbsDown className="w-4 h-4 mr-1" />
                  Poor
                </Button>
              </div>
            </div>
            
            {/* Sales Pitch Made - Show when rating is selected */}
            {callForm.rating && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Sales Pitch Made?</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={callForm.sales_pitch_made === true ? "default" : "outline"}
                    className={cn(
                      "flex-1",
                      callForm.sales_pitch_made === true && "bg-primary"
                    )}
                    onClick={() => setCallForm({ ...callForm, sales_pitch_made: true, no_pitch_reason: null })}
                    data-testid="sales-pitch-yes-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={callForm.sales_pitch_made === false ? "default" : "outline"}
                    className={cn(
                      "flex-1",
                      callForm.sales_pitch_made === false && "bg-gray-600"
                    )}
                    onClick={() => setCallForm({ ...callForm, sales_pitch_made: false, sales_pitch_type: [] })}
                    data-testid="sales-pitch-no-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    No
                  </Button>
                </div>
                
                {/* Sales Pitch Type - Show when sales_pitch_made is Yes */}
                {callForm.sales_pitch_made === true && (
                  <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                    <Label className="text-sm font-medium">Pitch Type (select all that apply)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: "Mobility", icon: Smartphone, label: "Mobility" },
                        { value: "Smart Home", icon: Home, label: "Smart Home" },
                        { value: "Custom Home", icon: Home, label: "Custom Home" },
                        { value: "Stream+", icon: Tv, label: "Stream+" },
                        { value: "Others", icon: HelpCircle, label: "Others" }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          variant={callForm.sales_pitch_type.includes(option.value) ? "default" : "outline"}
                          size="sm"
                          className="justify-start"
                          onClick={() => {
                            const current = callForm.sales_pitch_type;
                            const updated = current.includes(option.value)
                              ? current.filter(t => t !== option.value)
                              : [...current, option.value];
                            setCallForm({ ...callForm, sales_pitch_type: updated });
                          }}
                          data-testid={`pitch-type-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <option.icon className="w-4 h-4 mr-2" />
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* No Pitch Reason - Show when sales_pitch_made is No */}
                {callForm.sales_pitch_made === false && (
                  <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                    <Label className="text-sm font-medium">Reason</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={callForm.no_pitch_reason === "Customer Not Interested" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setCallForm({ ...callForm, no_pitch_reason: "Customer Not Interested" })}
                        data-testid="no-pitch-not-interested"
                      >
                        Customer Not Interested
                      </Button>
                      <Button
                        type="button"
                        variant={callForm.no_pitch_reason === "Others" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setCallForm({ ...callForm, no_pitch_reason: "Others" })}
                        data-testid="no-pitch-others"
                      >
                        Others
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Comments */}
            <div className="space-y-2">
              <Label htmlFor="comment">Comments</Label>
              <Textarea
                id="comment"
                placeholder="Add notes about this call..."
                value={callForm.comment}
                onChange={(e) => setCallForm({ ...callForm, comment: e.target.value })}
                data-testid="call-comment-input"
              />
            </div>
            
            {/* Call Outcome - Final Step */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-base font-semibold">Call Outcome</Label>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  type="button"
                  variant={callForm.call_outcome === "Complete Call" ? "default" : "outline"}
                  className={cn(
                    "justify-start h-12",
                    callForm.call_outcome === "Complete Call" && "bg-green-600 hover:bg-green-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, call_outcome: "Complete Call" })}
                  data-testid="outcome-complete-call"
                >
                  <CheckCircle className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Complete Call</p>
                    <p className="text-xs opacity-80">Customer answered and call was completed</p>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={callForm.call_outcome === "Left a Voicemail" ? "default" : "outline"}
                  className={cn(
                    "justify-start h-12",
                    callForm.call_outcome === "Left a Voicemail" && "bg-amber-600 hover:bg-amber-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, call_outcome: "Left a Voicemail", rating: null, sales_pitch_made: null })}
                  data-testid="outcome-voicemail"
                >
                  <Voicemail className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Left a Voicemail</p>
                    <p className="text-xs opacity-80">No answer - voicemail was left</p>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={callForm.call_outcome === "Call Not Picked Up" ? "default" : "outline"}
                  className={cn(
                    "justify-start h-12",
                    callForm.call_outcome === "Call Not Picked Up" && "bg-gray-600 hover:bg-gray-700"
                  )}
                  onClick={() => setCallForm({ ...callForm, call_outcome: "Call Not Picked Up", rating: null, sales_pitch_made: null })}
                  data-testid="outcome-not-picked-up"
                >
                  <PhoneOff className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Call Not Picked Up</p>
                    <p className="text-xs opacity-80">No answer - no voicemail left</p>
                  </div>
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLogCall} 
              disabled={!callForm.call_outcome}
              data-testid="submit-call-btn"
            >
              <PhoneForwarded className="w-4 h-4 mr-2" />
              Submit Call Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Customer List</DialogTitle>
            <DialogDescription>
              Upload an Excel file with customer data. Province can be detected from filename (e.g., AB.xlsx).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="mt-2"
                data-testid="import-file-input"
              />
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Expected columns:</p>
              <p className="text-xs">Customer, Customer Number, Alternate Number, Assigned Resources, Appointment Type, JOB DESCRIPTION, Street, City, PROV, Duration, Date, NCID, Call ID/WOID, Appointment Window</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={!importFile || isImporting} data-testid="import-submit-btn">
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>QA Module Settings</DialogTitle>
            <DialogDescription>
              Configure the QA call follow-up process and notifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Email Status Banner */}
            <div className={`p-3 rounded-lg border ${emailStatus?.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                {emailStatus?.configured ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">SendGrid Configured</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">SendGrid Not Configured</span>
                  </>
                )}
              </div>
              {!emailStatus?.configured && (
                <p className="text-xs text-amber-700 mt-1">
                  To send real emails, add <code className="bg-amber-100 px-1 rounded">SENDGRID_API_KEY</code> and <code className="bg-amber-100 px-1 rounded">SENDGRID_SENDER_EMAIL</code> to your backend .env file.
                </p>
              )}
            </div>

            {/* Call Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Call Settings</h3>
              <div className="space-y-2">
                <Label htmlFor="max_attempts">Maximum Call Attempts</Label>
                <Input
                  id="max_attempts"
                  type="number"
                  min={1}
                  max={10}
                  value={settingsForm.max_attempts}
                  onChange={(e) => setSettingsForm({ ...settingsForm, max_attempts: parseInt(e.target.value) || 3 })}
                  data-testid="settings-max-attempts"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="days_between">Days Between Retries</Label>
                <Input
                  id="days_between"
                  type="number"
                  min={1}
                  max={7}
                  value={settingsForm.days_between_retries}
                  onChange={(e) => setSettingsForm({ ...settingsForm, days_between_retries: parseInt(e.target.value) || 1 })}
                  data-testid="settings-days-between"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="auto_rollover">Auto Rollover Enabled</Label>
                <Switch
                  id="auto_rollover"
                  checked={settingsForm.auto_rollover_enabled}
                  onCheckedChange={(checked) => setSettingsForm({ ...settingsForm, auto_rollover_enabled: checked })}
                  data-testid="settings-auto-rollover"
                />
              </div>
            </div>

            {/* Notification Settings */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold">Notification Settings (Multiple CC Emails)</h3>
              <p className="text-xs text-muted-foreground">Add managers, supervisors who will be CC'd on POOR review notifications</p>
              
              {/* Existing manager emails */}
              {settingsForm.manager_emails?.length > 0 && (
                <div className="space-y-2">
                  {settingsForm.manager_emails.map((email, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-secondary/50 rounded">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => {
                          const updated = settingsForm.manager_emails.filter((_, i) => i !== index);
                          setSettingsForm({ ...settingsForm, manager_emails: updated });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new manager email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Add manager/supervisor email..."
                  value={settingsForm.newManagerEmail || ""}
                  onChange={(e) => setSettingsForm({ ...settingsForm, newManagerEmail: e.target.value })}
                  data-testid="settings-new-manager-email"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    if (settingsForm.newManagerEmail && settingsForm.newManagerEmail.includes("@")) {
                      if (!settingsForm.manager_emails?.includes(settingsForm.newManagerEmail)) {
                        setSettingsForm({
                          ...settingsForm,
                          manager_emails: [...(settingsForm.manager_emails || []), settingsForm.newManagerEmail],
                          newManagerEmail: ""
                        });
                      } else {
                        toast.error("Email already added");
                      }
                    } else {
                      toast.error("Please enter a valid email");
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Technician Email Mappings */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold">Technician Email Mappings</h3>
              <p className="text-xs text-muted-foreground">Map technician names to their email addresses for notifications</p>
              
              {/* Existing mappings */}
              {settingsForm.technician_emails.length > 0 && (
                <div className="space-y-2">
                  {settingsForm.technician_emails.map((mapping, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-secondary/50 rounded">
                      <span className="flex-1 text-sm font-medium">{mapping.technician_name}</span>
                      <span className="flex-1 text-sm text-muted-foreground">{mapping.technician_email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveTechMapping(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new mapping */}
              <div className="flex gap-2">
                <Input
                  placeholder="Technician Name"
                  value={newTechMapping.technician_name}
                  onChange={(e) => setNewTechMapping({ ...newTechMapping, technician_name: e.target.value })}
                  className="flex-1"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={newTechMapping.technician_email}
                  onChange={(e) => setNewTechMapping({ ...newTechMapping, technician_email: e.target.value })}
                  className="flex-1"
                />
                <Button variant="outline" size="sm" onClick={handleAddTechMapping}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSettings} data-testid="settings-save-btn">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={showCustomerDetail} onOpenChange={setShowCustomerDetail}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedCustomer.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedCustomer.customer_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Alt. Phone</p>
                  <p className="font-medium">{selectedCustomer.alternate_number || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Technician</p>
                  <p className="font-medium">{selectedCustomer.assigned_resources}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium">{selectedCustomer.appointment_type || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Province</p>
                  <Badge variant="outline">{selectedCustomer.province}</Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">City</p>
                  <p className="font-medium">{selectedCustomer.city || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  {getStatusBadge(selectedCustomer.status)}
                </div>
              </div>
              
              {selectedCustomer.job_description && (
                <div>
                  <p className="text-sm text-muted-foreground">Job Description</p>
                  <p className="text-sm">{selectedCustomer.job_description}</p>
                </div>
              )}
              
              {selectedCustomer.call_history && selectedCustomer.call_history.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Call History</p>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {selectedCustomer.call_history.map((call) => (
                        <div key={call.call_id} className="p-3 bg-secondary/50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium">{call.call_date}</p>
                              <p className="text-xs text-muted-foreground">by {call.agent_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {call.picked_up ? (
                                <Badge className="bg-green-100 text-green-800">Picked Up</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">No Answer</Badge>
                              )}
                            </div>
                          </div>
                          {call.picked_up && (
                            <div className="mt-2 flex items-center gap-2">
                              {getRatingBadge(call.rating)}
                              {call.sales_pitch_made && (
                                <Badge variant="outline">Sales Pitch</Badge>
                              )}
                              {call.rating === "POOR" && !call.notification_sent && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSendToTech(selectedCustomer.customer_id, call.call_id)}
                                  className="ml-auto"
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Send to Tech
                                </Button>
                              )}
                              {call.notification_sent && (
                                <Badge className="bg-blue-100 text-blue-800 ml-auto">
                                  <Mail className="w-3 h-3 mr-1" />
                                  Sent
                                </Badge>
                              )}
                            </div>
                          )}
                          {call.comment && (
                            <p className="mt-2 text-sm text-muted-foreground">{call.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProject ? "Edit Project" : "Create New Project"}</DialogTitle>
            <DialogDescription>
              {editingProject 
                ? "Update project settings and province assignments" 
                : "Create a project to group provinces together. Employees assigned to this project will only see calls from these provinces."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                placeholder="e.g., Western Region, Ontario Team"
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                data-testid="project-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Provinces</Label>
              <p className="text-xs text-muted-foreground">Select provinces that belong to this project</p>
              <div className="grid grid-cols-4 gap-2">
                {provinces.map(prov => (
                  <Button
                    key={prov}
                    type="button"
                    variant={projectForm.provinces?.includes(prov) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const current = projectForm.provinces || [];
                      const updated = current.includes(prov)
                        ? current.filter(p => p !== prov)
                        : [...current, prov];
                      setProjectForm({ ...projectForm, provinces: updated });
                    }}
                    data-testid={`project-province-${prov}`}
                  >
                    {prov}
                  </Button>
                ))}
              </div>
              {projectForm.provinces?.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Selected: {projectForm.provinces.join(", ")}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="supervisor-name">Supervisor Name</Label>
              <Input
                id="supervisor-name"
                placeholder="John Smith"
                value={projectForm.supervisor_name}
                onChange={(e) => setProjectForm({ ...projectForm, supervisor_name: e.target.value })}
                data-testid="project-supervisor-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="supervisor-email">Supervisor Email</Label>
              <Input
                id="supervisor-email"
                type="email"
                placeholder="supervisor@company.com"
                value={projectForm.supervisor_email}
                onChange={(e) => setProjectForm({ ...projectForm, supervisor_email: e.target.value })}
                data-testid="project-supervisor-email"
              />
              <p className="text-xs text-muted-foreground">
                This supervisor will be notified for POOR reviews in this project's provinces
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                placeholder="Optional project description..."
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                data-testid="project-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveProject} 
              disabled={!projectForm.name}
              data-testid="save-project-btn"
            >
              {editingProject ? "Update" : "Create"} Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
